<template>
    <div>
        <div class="p-6 pb-0">
            <BreadCrumbs :navigation="navigation" />
        </div>

        <div class="p-6">
            <div class="bg-white shadow-md rounded-lg p-6 border border-gray-200 mx-auto">
                <div class="mb-6 border-b pb-4">
                    <h1 class="text-2xl font-bold text-gray-800">Riconoscimento Documenti</h1>
                    <p class="text-sm text-gray-500">
                        Carica un documento per estrarne i dati con l'IA.
                    </p>
                </div>

                <div v-if="!isConfigured" class="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-sm">
                    Il modulo non è configurato correttamente. Verifica che i parametri Project ID / Processor ID di
                    Document AI siano impostati per il cliente.
                </div>

                <div v-if="checkingExisting" class="mb-6 p-4 bg-gray-50 border border-gray-200 rounded text-gray-600 text-sm flex items-center">
                    <v-icon name="hi-refresh" class="mr-2 animate-spin" />
                    Recupero stato dell'ultima elaborazione in corso...
                </div>

                <div
                    class="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors"
                    :class="dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300'"
                    @dragover.prevent="dragOver = true"
                    @dragleave.prevent="dragOver = false"
                    @drop.prevent="handleDrop"
                    @click="$refs.fileInput.click()"
                >
                    <input
                        ref="fileInput"
                        type="file"
                        class="hidden"
                        accept=".pdf,.jpg,.jpeg,.png,.gif,.tiff,.tif,.bmp,.webp"
                        @change="handleFileSelect"
                    />
                    <v-icon name="bi-file-earmark-arrow-up" scale="2" class="text-gray-400 mb-2" />
                    <p class="text-gray-600">
                        Trascina qui il documento oppure clicca per selezionarlo
                    </p>
                    <p v-if="selectedFile" class="mt-2 text-sm font-medium text-gray-800">
                        {{ selectedFile.name }}
                    </p>
                </div>

                <div v-if="loading" class="mt-4 p-4 bg-blue-50 border border-blue-200 rounded text-blue-700 text-sm flex items-center">
                    <v-icon name="hi-refresh" class="mr-2 animate-spin" />
                    Elaborazione in corso, il documento potrebbe richiedere alcuni minuti...
                </div>

                <div class="mt-4 flex justify-end gap-2">
                    <PrimaryButton v-if="formFields.length > 0" @click="downloadExcel">
                        <v-icon name="hi-download" class="mr-2" />
                        Scarica Excel
                    </PrimaryButton>
                    <PrimaryButton :disabled="!selectedFile || loading" @click="processDocument">
                        <v-icon v-if="loading" name="hi-refresh" class="mr-2 animate-spin" />
                        Elabora Documento
                    </PrimaryButton>
                </div>

                <div v-if="error" class="mt-4 p-4 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                    {{ error }}
                </div>

                <div v-if="entities.length > 0" class="mt-6 border rounded-lg overflow-hidden">
                    <button
                        type="button"
                        class="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left"
                        @click="entitiesExpanded = !entitiesExpanded"
                    >
                        <h2 class="text-lg font-semibold text-gray-800">Dati Estratti</h2>
                        <v-icon
                            name="hi-chevron-down"
                            class="text-gray-500 transition-transform"
                            :class="entitiesExpanded ? 'rotate-180' : ''"
                        />
                    </button>
                    <table v-if="entitiesExpanded" class="min-w-full divide-y divide-gray-200 border-t">
                        <thead class="bg-gray-50">
                            <tr>
                                <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campo</th>
                                <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valore</th>
                                <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Confidenza</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            <tr v-for="(entity, index) in flattenedEntities" :key="index" class="hover:bg-gray-50">
                                <td class="px-4 py-2 text-sm font-medium text-gray-800">{{ entity.type }}</td>
                                <td class="px-4 py-2 text-sm text-gray-700">{{ entity.normalizedValue || entity.mentionText }}</td>
                                <td class="px-4 py-2 text-sm text-gray-500">
                                    {{ entity.confidence != null ? Math.round(entity.confidence * 100) + '%' : '-' }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div v-if="formFields.length > 0" class="mt-6">
                    <h2 class="text-lg font-semibold text-gray-800 mb-3">Campi Modulo</h2>
                    <p class="text-sm text-gray-500 mb-3">
                        Puoi correggere i valori estratti prima di scaricare l'Excel.
                    </p>
                    <div class="overflow-x-scroll overflow-y-auto max-h-[500px] border rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50 sticky top-0 z-20">
                                <tr>
                                    <th class="sticky left-0 z-30 bg-gray-50 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border-r">Pagina</th>
                                    <th
                                        v-for="name in uniqueFieldNames"
                                        :key="name"
                                        class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                                    >
                                        {{ name }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-gray-200">
                                <tr v-for="row in pivotFormFields" :key="row.page" class="hover:bg-gray-50">
                                    <td class="sticky left-0 z-10 bg-white px-4 py-2 text-sm text-gray-500 whitespace-nowrap border-r">{{ row.page }}</td>
                                    <td v-for="name in uniqueFieldNames" :key="name" class="px-4 py-2 text-sm text-gray-700">
                                        <input
                                            v-model="row.cells[name].value"
                                            type="text"
                                            :title="row.cells[name].valueConfidence != null ? Math.round(row.cells[name].valueConfidence * 100) + '%' : ''"
                                            class="w-full min-w-[150px] border-gray-300 rounded text-sm focus:border-blue-400 focus:ring-blue-400"
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import axios from 'axios';
import ExcelJS from 'exceljs';

export default {
    components: {
        BreadCrumbs,
        PrimaryButton,
    },
    props: {
        moduleSlug: String,
        isConfigured: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            selectedFile: null,
            dragOver: false,
            loading: false,
            checkingExisting: true,
            error: null,
            entities: [],
            entitiesExpanded: false,
            formFields: [],
            pollingInterval: null,
        };
    },
    mounted() {
        this.checkExistingRequest();
    },
    beforeUnmount() {
        this.stopPolling();
    },
    computed: {
        flattenedEntities() {
            const flatten = (entities, prefix = '') => {
                let result = [];
                for (const entity of entities) {
                    const label = prefix ? `${prefix} / ${entity.type}` : entity.type;
                    if (entity.properties && entity.properties.length > 0) {
                        result = result.concat(flatten(entity.properties, label));
                    } else {
                        result.push({ ...entity, type: label });
                    }
                }
                return result;
            };

            return flatten(this.entities);
        },
        uniqueFieldNames() {
            return [...new Set(this.formFields.map((field) => field.name))].sort();
        },
        pivotFormFields() {
            const rowsByPage = {};
            for (const field of this.formFields) {
                if (!rowsByPage[field.page]) {
                    rowsByPage[field.page] = { page: field.page, cells: {} };
                }
                rowsByPage[field.page].cells[field.name] = field;
            }

            return Object.values(rowsByPage).sort((a, b) => a.page - b.page);
        },
        navigation() {
            return [
                {
                    name: 'Dashboard',
                    href: route('dashboard'),
                },
                {
                    name: 'Riconoscimento Documenti',
                    href: route('modules.home', 'riconoscimento-documenti'),
                },
            ];
        },
    },
    methods: {
        handleFileSelect(event) {
            const file = event.target.files[0];
            if (file) {
                this.selectedFile = file;
                this.entities = [];
                this.formFields = [];
                this.error = null;
            }
        },
        handleDrop(event) {
            this.dragOver = false;
            const file = event.dataTransfer.files[0];
            if (file) {
                this.selectedFile = file;
                this.entities = [];
                this.formFields = [];
                this.error = null;
            }
        },
        async checkExistingRequest() {
            this.checkingExisting = true;
            try {
                const response = await axios.get(route('modules.riconoscimento-documenti.status'));
                if (response.data.status === 'processing') {
                    this.loading = true;
                    this.startPolling();
                } else if (response.data.status === 'completed') {
                    this.applyResult(response.data);
                }
            } catch (err) {
            } finally {
                this.checkingExisting = false;
            }
        },
        async processDocument() {
            if (!this.selectedFile || this.loading) return;

            this.loading = true;
            this.error = null;
            this.entities = [];
            this.formFields = [];

            const formData = new FormData();
            formData.append('document', this.selectedFile);

            try {
                await axios.post(route('modules.riconoscimento-documenti.process'), formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });

                this.startPolling();
            } catch (err) {
                this.error = err.response?.data?.error || 'Errore durante l\'invio del documento.';
                this.loading = false;
            }
        },
        startPolling() {
            this.stopPolling();
            this.pollingInterval = setInterval(this.pollStatus, 5000);
        },
        stopPolling() {
            if (this.pollingInterval) {
                clearInterval(this.pollingInterval);
                this.pollingInterval = null;
            }
        },
        async pollStatus() {
            try {
                const response = await axios.get(route('modules.riconoscimento-documenti.status'));

                if (response.data.status === 'processing') {
                    return;
                }

                this.stopPolling();

                if (response.data.status === 'completed') {
                    this.applyResult(response.data);
                } else if (response.data.status === 'failed') {
                    this.error = response.data.error || 'Elaborazione del documento fallita.';
                }

                this.loading = false;
            } catch (err) {
                this.stopPolling();
                this.loading = false;
                this.error = err.response?.data?.error || 'Errore durante il controllo dello stato di elaborazione.';
            }
        },
        applyResult(data) {
            this.entities = data.entities || [];
            this.formFields = data.formFields || [];
            this.fillMissingFieldCells();
        },
        fillMissingFieldCells() {
            const names = [...new Set(this.formFields.map((field) => field.name))];
            const pages = [...new Set(this.formFields.map((field) => field.page))];
            const existing = new Set(this.formFields.map((field) => `${field.page}|${field.name}`));

            for (const page of pages) {
                for (const name of names) {
                    const key = `${page}|${name}`;
                    if (!existing.has(key)) {
                        this.formFields.push({
                            page,
                            name,
                            value: '',
                            valueConfidence: null,
                            isEntity: false,
                        });
                    }
                }
            }
        },
        async downloadExcel() {
            if (this.formFields.length === 0) return;

            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet('Document Data');

            sheet.columns = [
                { header: 'Pagina', key: 'page' },
                ...this.uniqueFieldNames.map((name) => ({ header: name, key: name })),
            ];

            for (const row of this.pivotFormFields) {
                const rowData = { page: row.page };
                for (const name of this.uniqueFieldNames) {
                    rowData[name] = row.cells[name]?.value || '';
                }
                sheet.addRow(rowData);
            }

            sheet.columns.forEach((column) => {
                let maxLength = column.header ? column.header.length : 10;
                column.eachCell({ includeEmpty: true }, (cell) => {
                    const length = cell.value ? cell.value.toString().length : 0;
                    if (length > maxLength) {
                        maxLength = length;
                    }
                });
                column.width = maxLength + 2;
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `estrazione_dati_${new Date().getTime()}.xlsx`;
            link.click();
            window.URL.revokeObjectURL(url);
        },
    },
};
</script>
